import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Bot, Zap, Target, TrendingUp, Users, Award, Database } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article8 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-purple-600 text-white mr-4">
              <Bot className="h-3 w-3 mr-1" />
              Robotics
            </Badge>
            <span className="text-sm text-gray-600">South China Morning Post</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 17, 2026
            <span className="mx-2">•</span>
            <span>5 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.8.title')}
          </h1>
          
          <img 
            src="https://www.consultancy.eu/illustrations/news/detail/2026-02-17-031759610-Ecommerce_braces_for_new_era_of_agentic_AI_shopping.webp" 
            alt="Agentic AI Shopping"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.8.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.8.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.8.p1')}
            </p>

            <p className="mb-6">
              {t('article.8.p2')}
            </p>

            <div className="bg-purple-50 p-6 rounded-lg my-8 border border-purple-100">
              <h3 className="font-bold text-purple-900 mb-2 flex items-center">
                <Target className="mr-2" />
                {t('article.8.highlight.title')}
              </h3>
              <p className="text-sm text-purple-800">
                {t('article.8.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.8.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.8.p3')}
            </p>

            <p className="mb-6">
              {t('article.8.p4')}
            </p>

            <div className="bg-purple-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-purple-900 mb-4">{language === 'ko' ? 'AI 쇼핑 혁명' : 'AI Shopping Revolution'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-purple-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Database className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '자율 AI 에이전트' : 'Autonomous AI Agents'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '정의:' : 'Definition:'}</strong> {language === 'ko' ? '독립적 사고와 결정' : 'Independent reasoning and decisions'}</li>
                    <li><strong>{language === 'ko' ? '기능:' : 'Capability:'}</strong> {language === 'ko' ? '사용자 대신 쇼핑' : 'Shopping on behalf of users'}</li>
                    <li><strong>{language === 'ko' ? '예시:' : 'Example:'}</strong> {language === 'ko' ? '$80 이하 방수 백팩 찾기' : 'Find waterproof backpack under $80'}</li>
                    <li><strong>{language === 'ko' ? '예측:' : 'Prediction:'}</strong> {language === 'ko' ? '2030년 대부분 거래 AI 관리' : '2030 majority transactions AI-managed'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '데이터 우선순위 변화' : 'Data Priority Shift'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '기존 방식:' : 'Traditional:'}</strong> {language === 'ko' ? '브랜드 이미지와 감정적 마케팅' : 'Brand image and emotional marketing'}</li>
                    <li><strong>{language === 'ko' ? '새로운 방식:' : 'New Approach:'}</strong> {language === 'ko' ? '기계 읽기 가능한 데이터' : 'Machine-readable data'}</li>
                    <li><strong>{language === 'ko' ? '소비자 수용:' : 'Consumer Adoption:'}</strong> {language === 'ko' ? '25-30% 이미 편안함' : '25-30% already comfortable'}</li>
                    <li><strong>{language === 'ko' ? '영향:' : 'Impact:'}</strong> {language === 'ko' ? '제품 반품 감소, 데이터 오류 위험' : 'Reduced returns, data error risks'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.8.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.8.p5')}
            </p>

            <p className="mb-6">
              {t('article.8.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.8.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.8.p7')}
            </p>

            <p className="mb-6">
              {t('article.8.p8')}
            </p>

            <div className="bg-purple-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-purple-900 mb-4">{language === 'ko' ? 'AI 쇼핑 혁명' : 'AI Shopping Revolution'}</h3>
              <div className="space-y-4 text-sm text-purple-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Bot className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '자율 AI 에이전트' : 'Autonomous AI Agents'}
                  </h4>
                  <p>{language === 'ko' ? 
                    'IG&H 분석에 따르면 자율 AI 에이전트가 주요 상거래 게이트웨이가 되고 있으며, 25-30%의 소비자가 AI 쇼핑에 편안함을 느끼고 있습니다.' : 
                    'IG&H analysis reveals autonomous AI agents are becoming primary commerce gateway, with 25-30% of consumers comfortable with AI shopping.'
                  }</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Database className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '데이터 우선순위' : 'Data Priority Shift'}
                  </h4>
                  <p>{language === 'ko' ? 
                    '소매업체들이 기계 판독 가능한 제품 데이터를 우선시하도록 강요받고 있으며, 2030년까지 대부분의 쇼핑이 AI로 관리될 것으로 예측됩니다.' : 
                    'Retailers forced to prioritize machine-readable product data, with majority of shopping predicted to be AI-managed by 2030.'
                  }</p>
                </div>
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} South China Morning Post - Unitree eyes 20,000-robot output in 2026 after gala.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://www.consultancy.eu/news/13240/ecommerce-braces-for-new-era-of-agentic-ai-shopping" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article8;